import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { ApolloProvider } from '@apollo/client';
import { useEffect } from 'react';

import ScrollContext from './components/ScrollContext';
import App from './components/App';
import { createClient } from './lib/graphql/client';
import { createRoutesMap } from './lib/routes';
import { configureStore } from './store';
import { setTimeZone } from './store/intl/actions';
import { AnalyticsContextProvider } from './lib/analytics/context';
import { ExperimentationProvider } from './lib/experimentation';
import { whenLazyComponentsLoaded } from './lib/tracked-lazy';
import { rememberOpenedFromApp } from './lib/opened-from-app';

// Before anything strips the query string (e.g. a redeemed ?token=).
rememberOpenedFromApp();

const client = createClient(
  {
    // This is broken in Apollo client
    // https://github.com/apollographql/apollo-client/pull/5839
    // ssrForceFetchDelay: 100,
    connectToDevTools: true,
  },
  { initialState: window.__APOLLO_STATE__ }
);
const { store } = configureStore(window.REDUX_STATE, {
  routesMap: createRoutesMap(window.PAGES, { graphqlClient: client }),
});

const InitAfterRender = (props) => {
  useEffect(() => {
    // Dispatching while lazy components are still hydrating would make React
    // discard their server-rendered HTML (React error #421).
    whenLazyComponentsLoaded().then(() => {
      store.dispatch(
        setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
      );
    });
  }, []);
  return props.children;
};

const render = (Component) => {
  hydrateRoot(
    document.getElementById('hub'),
    <InitAfterRender>
      <ApolloProvider client={client}>
        <Provider store={store}>
          <HelmetProvider>
            <ScrollContext>
              <AnalyticsContextProvider>
                <ExperimentationProvider>
                  <Component />
                </ExperimentationProvider>
              </AnalyticsContextProvider>
            </ScrollContext>
          </HelmetProvider>
        </Provider>
      </ApolloProvider>
    </InitAfterRender>
  );
};

render(App);
